import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarBlank } from "@phosphor-icons/react";
import { format } from "date-fns";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { Button } from "../ui/button";

// Master Prompt Part 2 (Wes, Sep 17 2026) — "Deals Submitted Summary": daily rows, by rep,
// trust + funding totals, with a date-range filter. Shared between Admin and Manager dashboards.
// Build S3 (Sep 18 2026) — plain text date boxes replaced with a proper calendar range picker.
export function DealsSummarySection({ apiBase }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [draftRange, setDraftRange] = useState({ from: undefined, to: undefined });
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = (activeRange) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activeRange?.from) params.set("start_date", format(activeRange.from, "yyyy-MM-dd"));
    if (activeRange?.to) params.set("end_date", format(activeRange.to, "yyyy-MM-dd"));
    api.get(`${apiBase}/deals-summary?${params.toString()}`)
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(range); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openPicker = (open) => {
    if (open) setDraftRange(range);
    setPickerOpen(open);
  };

  const applyFilter = () => {
    setRange(draftRange);
    setPickerOpen(false);
    load(draftRange);
  };

  const rangeLabel = range.from
    ? range.to
      ? `${format(range.from, "MMM d, yyyy")} – ${format(range.to, "MMM d, yyyy")}`
      : format(range.from, "MMM d, yyyy")
    : "All dates";

  return (
    <div className="mt-8" data-testid="deals-summary-section">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
        <h2
          className="font-serif text-xl text-igc-cream tracking-tight"
          title="This section lists every Trust and Funding deal that was submitted, grouped by rep and by day, with running totals. Use it to see who is closing deals, how many of each type, and when."
        >
          Deals Submitted Summary
        </h2>
        <div className="flex items-center gap-2 text-xs">
          <Popover open={pickerOpen} onOpenChange={openPicker}>
            <PopoverTrigger asChild>
              <button
                data-testid="deals-summary-date-range-trigger"
                title="Click to open a calendar and pick the start and end date you want to see deals for. Select a start date, then an end date — the range between them highlights — then hit Confirm to filter the table below. Works the same way on a phone or a computer."
                className="flex items-center gap-2 bg-igc-bg border border-igc-gold/20 px-3 py-1.5 text-igc-cream hover:border-igc-gold/50 transition-colors"
              >
                <CalendarBlank size={14} className="text-igc-goldText" />
                {rangeLabel}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-igc-surface border-igc-gold/20" align="end">
              <Calendar
                mode="range"
                selected={draftRange}
                onSelect={(r) => setDraftRange(r || { from: undefined, to: undefined })}
                numberOfMonths={1}
                className="text-igc-cream"
                data-testid="deals-summary-calendar"
              />
              <div className="flex items-center justify-between gap-2 p-3 border-t border-igc-gold/10">
                <button
                  data-testid="deals-summary-clear-range-button"
                  onClick={() => setDraftRange({ from: undefined, to: undefined })}
                  title="Clears the selected date range so the table shows deals from all time again."
                  className="text-igc-muted hover:text-igc-goldText text-xs"
                >
                  Clear
                </button>
                <Button data-testid="deals-summary-confirm-range-button" onClick={applyFilter} title="Applies the date range you picked to the table below." className="h-8 text-xs bg-igc-gold hover:bg-igc-goldHover text-black">
                  Confirm
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <p className="text-igc-muted text-xs mb-3">Trust and funding deals submitted by each rep, one row per day.</p>
      {loading ? (
        <p className="text-igc-muted text-sm" data-testid="deals-summary-loading">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-igc-muted text-sm" data-testid="deals-summary-empty">No deals submitted in this range yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="deals-summary-table">
            <thead>
              <tr className="text-igc-muted text-xs uppercase tracking-widest text-left border-b border-igc-gold/15">
                <th className="py-2 pr-4" title="The calendar day the deal(s) were submitted on.">Date</th>
                <th className="py-2 pr-4" title="The rep who submitted the deal, with their rep code in parentheses.">Rep</th>
                <th className="py-2 pr-4" title="How many Trust & Asset Protection deals (Basic Living Trust or Private Trust) this rep submitted on this day.">Trust Deals</th>
                <th className="py-2 pr-4" title="How many Business Funding deals this rep submitted on this day.">Funding Deals</th>
                <th className="py-2 pr-4" title="Trust Deals plus Funding Deals added together for this rep, this day.">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`${r.rep_code}-${r.date}-${idx}`} className="border-b border-igc-gold/10" data-testid={`deals-summary-row-${idx}`}>
                  <td className="py-2.5 pr-4 text-igc-muted">{r.date}</td>
                  <td className="py-2.5 pr-4 text-igc-cream">{r.rep_display_name} <span className="text-igc-muted text-xs">({r.rep_code})</span></td>
                  <td className="py-2.5 pr-4 text-igc-goldText">{r.trust_count}</td>
                  <td className="py-2.5 pr-4 text-igc-success">{r.funding_count}</td>
                  <td className="py-2.5 pr-4 text-igc-cream font-medium">{r.total_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
